import { getMessaging, getToken, onMessage, isSupported, type MessagePayload } from 'firebase/messaging'
import { doc, updateDoc, arrayUnion } from 'firebase/firestore'
import app from './config'
import { db } from './config'

/**
 * Lazily initialised messaging instance.
 * Returns null when FCM is not supported (e.g. Safari without push, Firefox private mode).
 */
let messagingInstance: ReturnType<typeof getMessaging> | null = null

async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance
  try {
    const supported = await isSupported()
    if (!supported) {
      console.warn('Firebase Messaging is not supported in this browser.')
      return null
    }
    messagingInstance = getMessaging(app)
    return messagingInstance
  } catch (err) {
    console.warn('Failed to initialise Firebase Messaging:', err)
    return null
  }
}

/**
 * Request notification permission, obtain an FCM token, and persist it
 * to the user's Firestore document under `fcmTokens` (array).
 *
 * @returns The FCM token string, or null if not supported / denied.
 */
export async function requestNotificationPermission(userId: string): Promise<string | null> {
  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      console.log('Notification permission denied.')
      return null
    }

    const messaging = await getMessagingInstance()
    if (!messaging) return null

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
    if (!vapidKey) {
      console.warn('VITE_FIREBASE_VAPID_KEY is not set. FCM token cannot be obtained.')
      return null
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js'),
    })

    if (token) {
      // Save token to user's Firestore doc (arrayUnion avoids duplicates)
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        fcmTokens: arrayUnion(token),
      })
    }

    return token
  } catch (err) {
    console.error('Failed to get FCM token:', err)
    return null
  }
}

/**
 * Listen for push messages while the app is in the foreground.
 * Returns an unsubscribe function, or null if messaging is not supported.
 */
export async function onForegroundMessage(
  callback: (payload: MessagePayload) => void
): Promise<(() => void) | null> {
  const messaging = await getMessagingInstance()
  if (!messaging) return null

  const unsubscribe = onMessage(messaging, callback)
  return unsubscribe
}
